// AMOS API services for React 19 with TypeScript
import type { AxiosInstance, AxiosResponse } from 'axios';
import { offlineStorage } from './offlineStorage';
import type { ErrorHandlerOptions } from './error_handler';

// Type definitions
type ErrorHandler = <T = any>(
  _request: Promise<AxiosResponse<T>>,
  _options?: ErrorHandlerOptions
) => Promise<AxiosResponse<T> | string>;

interface AmosLoginResponse {
  clientToken: string;
  clientRefreshToken: string;
  clientId: string;
  isRegisteredAmos: boolean;
}

interface CountryResponse {
  data: any[];
  fromCache?: boolean;
}

interface RequestResponse {
  data: any;
  fromCache?: boolean;
}

// Factory function to create AMOS services
export const amosServices = (
  _env: string,
  api: AxiosInstance,
  errorHandler: ErrorHandler
) => ({
  amosLogin: async (): Promise<AxiosResponse<AmosLoginResponse> | string> => {
    const url = `/api/v1/amos/clients/login`;
    return errorHandler<AmosLoginResponse>(api.post(url));
  },

  amosLoginAdult: async (): Promise<
    AxiosResponse<AmosLoginResponse> | string
  > => {
    const url = `/api/v1/amos/clients/login-adult`;
    return errorHandler<AmosLoginResponse>(api.post(url));
  },

  validateAccessCode: async (
    accessCode: string
  ): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/validate-access-code`;
    const payload = { accessCode: accessCode?.toUpperCase() };

    return errorHandler(api.post(url, payload));
  },

  amosCreate: async (payload: any): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/clients`;
    return errorHandler(api.post(url, payload));
  },

  amosCreateAdultClient: async (
    payload: any
  ): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/clients/adult`;
    return errorHandler(api.post(url, payload));
  },

  amosUpdateClient: async (payload: any): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/clients`;
    return errorHandler(api.put(url, payload));
  },

  amosLogout: async (token: string): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/clients/logout`;
    return errorHandler(api.post(url, { clientToken: token }));
  },

  amosRefreshToken: async (
    data: string
  ): Promise<AxiosResponse<AmosLoginResponse> | string> => {
    const url = `/api/v1/amos/clients/refresh-token`;
    return errorHandler<AmosLoginResponse>(
      api.post(url, { clientRefreshToken: data })
    );
  },

  getFamilyMembers: async (token: string): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/dependents?clientToken=${token}`;
    return errorHandler(api.get(url));
  },

  getCountries: async (
    idiomAmos: string
  ): Promise<CountryResponse | AxiosResponse | string> => {
    const url = `/api/v1/amos/countries?language=${idiomAmos}`;
    try {
      const response = await errorHandler(api.get(url));

      // Store successful response in offline storage
      if (response && typeof response !== 'string' && response.data) {
        await offlineStorage.storeCountries(idiomAmos, response.data);
      }

      return response;
    } catch (error) {
      // If online request fails and we're offline, try to get cached data
      if (!navigator.onLine) {
        const cachedData = await offlineStorage.getCountries(idiomAmos);
        if (cachedData) {
          return { data: cachedData, fromCache: true };
        }
      }
      throw error;
    }
  },

  getTime: async (timeZone: string): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/time?timeZone=${timeZone}`;
    return errorHandler(api.get(url));
  },

  amosStartVideoCall: async (
    id: string,
    clientToken: string
  ): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/videocalls/${id}/start`;
    return errorHandler(api.post(url, clientToken));
  },

  amosEndVideoCall: async (
    id: string,
    sendData: any
  ): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/videocalls/${id}/end`;
    return errorHandler(api.post(url, sendData));
  },

  postAmosDependets: async (sendData: any): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/dependents/create`;
    return errorHandler(api.post(url, sendData));
  },

  putAmosDependets: async (
    sendData: any,
    dependentId: string
  ): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/dependents/${dependentId}`;
    return errorHandler(api.put(url, sendData));
  },

  deleteAmosDependets: async (
    sendData: any,
    dependentId: string
  ): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/dependents/${dependentId}`;
    return errorHandler(api.delete(url, { data: sendData }));
  },

  getTimezone: async (idiomAmos: string): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/timezones?language=${idiomAmos}`;
    return errorHandler(api.get(url));
  },

  getClientAndDependents: async (
    clientToken: string,
    clientId: string
  ): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/clients?clientToken=${clientToken}&clientId=${clientId}`;
    return errorHandler(api.get(url));
  },

  getAdultDetails: async (
    clientToken: string,
    clientId: string
  ): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/clients?clientToken=${clientToken}&clientId=${clientId}`;
    return errorHandler(api.get(url));
  },

  getAppointments: async (
    idiomAmos: string,
    timeZone: string,
    clientToken: string,
    country: string,
    locale: string
  ): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/videocalls-availability?timeZone=${timeZone}&clientToken=${clientToken}&language=${idiomAmos}&country=${country}&locale=${locale}`;
    return errorHandler(api.get(url));
  },

  requestVideoCall: async (sendData: any): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/videocalls/create`;
    return errorHandler(api.post(url, sendData));
  },

  requestPhoneCall: async (
    sendData: any,
    countryofResidency = ''
  ): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/consultations/create`;
    return errorHandler(api.post(url, sendData), {
      requestType: 'requestPhoneCall',
      countryResidence: countryofResidency,
    });
  },

  getRequestsVideo: async (
    clientToken: string,
    userEmail?: string,
    withFiles = false
  ): Promise<RequestResponse | AxiosResponse | string> => {
    const url = `/api/v1/amos/videocalls?clientToken=${clientToken}&files=${withFiles}`;
    try {
      const response = await errorHandler(api.get(url));

      return response;
    } catch (error) {
      // If online request fails and we're offline, try to get cached data
      if (!navigator.onLine && userEmail) {
        const cachedData = await offlineStorage.getVideoRequests(userEmail);
        if (cachedData) {
          return { data: cachedData, fromCache: true };
        }
      }
      throw error;
    }
  },

  getRequestsPhone: async (
    clientToken: string,
    userEmail?: string,
    withFiles = false
  ): Promise<RequestResponse | AxiosResponse | string> => {
    const url = `/api/v1/amos/consultations?clientToken=${clientToken}&files=${withFiles}`;
    try {
      const response = await errorHandler(api.get(url));

      return response;
    } catch (error) {
      // If online request fails and we're offline, try to get cached data
      if (!navigator.onLine && userEmail) {
        const cachedData = await offlineStorage.getPhoneRequests(userEmail);
        if (cachedData) {
          return { data: cachedData, fromCache: true };
        }
      }
      throw error;
    }
  },

  getPhoneCall: async (
    phoneCallId: string,
    clientToken: string
  ): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/consultations/${phoneCallId}?clientToken=${clientToken}&files=true`;
    return errorHandler(api.get(url));
  },

  getVideocall: async (
    videocallId: string,
    clientToken: string
  ): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/videocalls/${videocallId}?clientToken=${clientToken}&files=true`;
    return errorHandler(api.get(url));
  },

  cancelPhoneCall: async (
    sendData: any,
    phoneCallId: string
  ): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/consultations/${phoneCallId}/cancel`;
    return errorHandler(api.post(url, sendData));
  },

  cancelVideoCall: async (
    sendData: any,
    videocallId: string
  ): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/videocalls/${videocallId}/cancel`;
    return errorHandler(api.post(url, sendData));
  },

  getDoctor: async (doctorId: string): Promise<AxiosResponse | string> => {
    const url = `/api/v1/amos/doctors/${doctorId}`;
    return errorHandler(api.get(url));
  },

  getVblock: async (file: File): Promise<any> => {
    const url = `/api/v1/amos/vblock/upload`;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Vblock upload response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Vblock upload error:', error);
      return errorHandler(Promise.reject(error));
    }
  },
});
